// Settings → Account → Two-step login (admin PR 2): status, new backup codes, set up
// again on a new phone. Each action asks for a current code (app or backup) first, so
// a signed-in browser left open can't move two-step login elsewhere.

import { useEffect, useState, type FormEvent } from 'react';
import { ApiError, getTwoStep, newBackupCodes, resetupFinish, resetupStart, type TwoStepSecret, type TwoStepStatus } from '../api';
import { cleanBackupCode } from '../two-step';
import { longDate } from '../time';
import { BackupCodesView, CodeBox, SecretView, codeReady } from './TwoStep';

type Flow =
  | { kind: 'idle' }
  | { kind: 'confirm'; next: 'codes' | 'move' }
  | { kind: 'codes'; codes: string[] }
  | { kind: 'move'; data: TwoStepSecret };

const sinceText = (iso: string | null) => (iso ? longDate(iso) : '');

export function TwoStepSetting({ email }: { email: string }) {
  const [status, setStatus] = useState<TwoStepStatus | null>(null);
  const [flow, setFlow] = useState<Flow>({ kind: 'idle' });
  const [code, setCode] = useState('');
  const [backup, setBackup] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: 'ok' | 'bad'; text: string } | null>(null);

  useEffect(() => {
    getTwoStep().then(setStatus, () => setStatus(null));
  }, []);

  // normal users: nothing to show yet (optional two-step login for them comes later)
  if (!status || (!status.on && !status.required)) return null;

  const start = (next: 'codes' | 'move') => {
    setFlow({ kind: 'confirm', next });
    setCode('');
    setBackup(false);
    setMessage(null);
  };
  const cancel = () => {
    setFlow({ kind: 'idle' });
    setCode('');
  };
  const typed = () => (backup ? cleanBackupCode(code) ?? '' : code);

  async function confirm(e: FormEvent) {
    e.preventDefault();
    if (flow.kind !== 'confirm' || busy || !codeReady(code, backup)) return;
    setBusy(true);
    setMessage(null);
    try {
      if (flow.next === 'codes') {
        const r = await newBackupCodes(typed());
        setStatus(r.twoStep);
        setFlow({ kind: 'codes', codes: r.backupCodes });
      } else {
        const data = await resetupStart(typed());
        setFlow({ kind: 'move', data });
        setBackup(false);
      }
      setCode('');
    } catch (err) {
      setMessage({ tone: 'bad', text: err instanceof ApiError ? err.message : 'Couldn’t check the code. Try again.' });
      setCode('');
    } finally {
      setBusy(false);
    }
  }

  async function finishMove(e: FormEvent) {
    e.preventDefault();
    if (flow.kind !== 'move' || busy || code.length !== 6) return;
    setBusy(true);
    setMessage(null);
    try {
      setStatus(await resetupFinish(code));
      setFlow({ kind: 'idle' });
      setMessage({ tone: 'ok', text: 'Two-step login now uses the new app. Codes from the old one no longer work.' });
    } catch (err) {
      const expired = err instanceof ApiError && err.code === 'expired';
      setMessage({ tone: 'bad', text: err instanceof ApiError ? err.message : 'Couldn’t check the code. Try again.' });
      if (expired) setFlow({ kind: 'idle' });
    } finally {
      setCode('');
      setBusy(false);
    }
  }

  const low = status.on && status.backupCodesLeft <= 2;

  return (
    <div className="setting setting-stack">
      <div className="setting-head">
        <div className="setting-text">
          <span className="setting-name">Two-step login</span>
          <span className="setting-help">
            {status.on
              ? `On since ${sinceText(status.since)}: logging in needs your password and a code from your authenticator app.`
              : 'Not set up yet. You set it up at your next login.'}
          </span>
          {status.on && (
            <span className={`setting-help${low ? ' setting-warn' : ''}`}>
              {status.backupCodesLeft === 0
                ? 'No backup codes left. Make new ones.'
                : `${status.backupCodesLeft} backup code${status.backupCodesLeft === 1 ? '' : 's'} left.`}
            </span>
          )}
        </div>
      </div>

      {status.on && flow.kind === 'idle' && (
        <div className="ts-setting-actions">
          <button className="btn btn-small" type="button" onClick={() => start('codes')}>
            New backup codes
          </button>
          <button className="btn btn-small" type="button" onClick={() => start('move')}>
            Set up again
          </button>
        </div>
      )}

      {flow.kind === 'confirm' && (
        <form className="password-form" onSubmit={confirm} noValidate>
          <p className="setting-note">
            {flow.next === 'codes'
              ? 'New backup codes replace all your old ones. First, confirm it’s you:'
              : 'Moving to a new phone? First, confirm it’s you with a code from your current app — or a backup code if the old phone is gone:'}
          </p>
          <CodeBox key={backup ? 'b' : 'a'} value={code} onChange={setCode} backup={backup} label={backup ? 'Backup code' : 'Code from your app'} name="confirm-code" onLetters={(typed) => { setBackup(true); setCode(typed); }} />
          <button className="link-btn" type="button" onClick={() => { setBackup(!backup); setCode(''); }}>
            {backup ? 'Use the code from my app' : 'Use a backup code'}
          </button>
          <div className="password-actions">
            <button className="btn btn-quiet btn-small" type="button" onClick={cancel} disabled={busy}>
              Cancel
            </button>
            <button className="btn btn-primary btn-small" type="submit" disabled={busy || !codeReady(code, backup)}>
              {busy ? 'Checking…' : 'Continue'}
            </button>
          </div>
        </form>
      )}

      {flow.kind === 'codes' && (
        <div className="password-form">
          <p className="setting-note">Your new backup codes. They are shown only now; the old ones no longer work.</p>
          <BackupCodesView codes={flow.codes} account={email} />
          <div className="password-actions">
            <button className="btn btn-primary btn-small" type="button" onClick={() => setFlow({ kind: 'idle' })}>
              I’ve saved them
            </button>
          </div>
        </div>
      )}

      {flow.kind === 'move' && (
        <form className="password-form" onSubmit={finishMove} noValidate>
          <p className="setting-note">Scan this with the authenticator app on your new phone, then type the code it shows. Your backup codes stay the same.</p>
          <SecretView data={flow.data} />
          <CodeBox value={code} onChange={setCode} label="Code from the new app" autoFocus={false} name="new-code" />
          <div className="password-actions">
            <button className="btn btn-quiet btn-small" type="button" onClick={cancel} disabled={busy}>
              Cancel
            </button>
            <button className="btn btn-primary btn-small" type="submit" disabled={busy || code.length !== 6}>
              {busy ? 'Checking…' : 'Use the new app'}
            </button>
          </div>
        </form>
      )}

      {message && (
        <p className={`setting-msg setting-msg-${message.tone}`} role={message.tone === 'bad' ? 'alert' : 'status'}>
          {message.text}
        </p>
      )}
    </div>
  );
}
